import { createContext, useContext, useEffect, useState } from "react";
import { apiFetch } from "../api";
import { useI18n } from "../i18n/I18nContext";

const TrackingCtx = createContext(null);

const STATUT_OPTIONS = ["NEUF", "EN COURS", "FINI"];

function toInputDate(value) {
  if (!value) return "";
  if (/^\d{4}-\d{2}-\d{2}$/.test(value)) return value;
  const m = String(value).match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
  if (m) return `${m[3]}-${m[2]}-${m[1]}`;
  return "";
}

function normalizeStatut(value) {
  const raw = String(value || "")
    .trim()
    .toUpperCase()
    .replace(/_/g, " ");
  if (raw === "ENCOURS") return "EN COURS";
  if (STATUT_OPTIONS.includes(raw)) return raw;
  return "NEUF";
}

const inputStyle = {
  width: "100%",
  minWidth: 120,
  padding: "4px 6px",
  fontSize: 12,
  border: "1px solid #ccc",
  borderRadius: 4,
  boxSizing: "border-box",
};

export function TrackingFields({
  pn,
  apiPath,
  dateEnCours,
  dateFini,
  placageRestant,
  statut,
  onSaved,
  children,
}) {
  const [draft, setDraft] = useState({
    dateEnCours: toInputDate(dateEnCours),
    dateFini: toInputDate(dateFini),
    placageRestant: placageRestant ?? "",
    statut: normalizeStatut(statut),
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    setDraft({
      dateEnCours: toInputDate(dateEnCours),
      dateFini: toInputDate(dateFini),
      placageRestant: placageRestant ?? "",
      statut: normalizeStatut(statut),
    });
  }, [dateEnCours, dateFini, placageRestant, statut, pn]);

  const dirty =
    draft.dateEnCours !== toInputDate(dateEnCours) ||
    draft.dateFini !== toInputDate(dateFini) ||
    String(draft.placageRestant ?? "") !== String(placageRestant ?? "") ||
    draft.statut !== normalizeStatut(statut);

  async function save() {
    if (!pn || !dirty) return;
    setSaving(true);
    setError("");
    try {
      let quantityField = "NombreDePlacageRestant";
      if (apiPath && apiPath.includes("/resine/")) quantityField = "MasseRestante";
      if (apiPath && apiPath.includes("/amorceur/")) quantityField = "Conditionnement";
      const body = {
        PN: pn,
        Date_en_cours: draft.dateEnCours || "",
        Date_fini: draft.dateFini || "",
        Statut: draft.statut,
      };
      if (String(draft.placageRestant).trim() !== "") {
        // Saisie brute : le serveur valide (virgule acceptée) et refuse une valeur illisible
        body[quantityField] = String(draft.placageRestant).trim();
      }
      const res = await apiFetch(apiPath, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
      const data = await res.json();
      if (!res.ok || data.status !== "success") {
        throw new Error(data.message || "Erreur enregistrement");
      }
      onSaved?.({
        dateEnCours: draft.dateEnCours,
        dateFini: draft.dateFini,
        placageRestant: draft.placageRestant,
        etat: data.updates?.Statut || draft.statut,
      });
    } catch (e) {
      setError(e.message || "Erreur");
    } finally {
      setSaving(false);
    }
  }

  return (
    <TrackingCtx.Provider
      value={{ draft, setDraft, save, saving, dirty, error, pn }}
    >
      {children}
    </TrackingCtx.Provider>
  );
}

function useTracking() {
  const ctx = useContext(TrackingCtx);
  if (!ctx) throw new Error("TrackingFields provider manquant");
  return ctx;
}

export function TrackingDateEnCoursInput() {
  const { draft, setDraft, pn } = useTracking();
  if (!pn) return null;
  return (
    <input
      type="date"
      value={draft.dateEnCours}
      onChange={(e) => setDraft((d) => ({ ...d, dateEnCours: e.target.value }))}
      style={inputStyle}
      title="Date en cours"
    />
  );
}

export function TrackingDateFiniInput() {
  const { draft, setDraft, pn } = useTracking();
  if (!pn) return null;
  return (
    <input
      type="date"
      value={draft.dateFini}
      onChange={(e) => setDraft((d) => ({ ...d, dateFini: e.target.value }))}
      style={inputStyle}
      title="Date fini"
    />
  );
}

export function TrackingPlacageRestantInput() {
  const { draft, setDraft, pn } = useTracking();
  if (!pn) return null;
  return (
    <input
      type="number"
      min="0"
      step="1"
      value={draft.placageRestant}
      onChange={(e) => setDraft((d) => ({ ...d, placageRestant: e.target.value }))}
      style={inputStyle}
      title="Nb placage restant"
    />
  );
}

export function TrackingMasseRestanteInput() {
  const { draft, setDraft, pn } = useTracking();
  if (!pn) return null;
  return (
    <input
      type="number"
      min="0"
      step="0.01"
      value={draft.placageRestant}
      onChange={(e) => setDraft((d) => ({ ...d, placageRestant: e.target.value }))}
      style={inputStyle}
      title="Quantite restante"
    />
  );
}

export function TrackingConditionnementInput() {
  const { draft, setDraft, pn } = useTracking();
  if (!pn) return null;
  return (
    <input
      type="number"
      min="0"
      step="0.01"
      value={draft.placageRestant}
      onChange={(e) => setDraft((d) => ({ ...d, placageRestant: e.target.value }))}
      style={inputStyle}
      title="Conditionnement restant"
    />
  );
}

export function TrackingStatutSelect() {
  const { draft, setDraft, pn } = useTracking();
  if (!pn) return null;
  return (
    <select
      value={draft.statut}
      onChange={(e) => setDraft((d) => ({ ...d, statut: e.target.value }))}
      style={{ ...inputStyle, minWidth: 110, fontWeight: 700 }}
      title="Statut"
    >
      {STATUT_OPTIONS.map((opt) => (
        <option key={opt} value={opt}>
          {opt}
        </option>
      ))}
    </select>
  );
}

export function TrackingSaveButton() {
  const { dirty, save, saving, error, pn } = useTracking();
  const { t } = useI18n();
  if (!pn || !dirty) {
    return error ? <span style={{ color: "#c00", fontSize: 11 }}>{error}</span> : null;
  }
  return (
    <div style={{ marginTop: 4 }}>
      <button
        type="button"
        onClick={save}
        disabled={saving}
        style={{
          padding: "3px 8px",
          fontSize: 11,
          fontWeight: 700,
          border: "1px solid #111",
          borderRadius: 4,
          background: "#E6FF3E",
          cursor: saving ? "wait" : "pointer",
        }}
      >
        {saving ? "…" : t("common.saveTracking")}
      </button>
      {error && <div style={{ color: "#c00", fontSize: 11 }}>{error}</div>}
    </div>
  );
}
